import { ref, computed, watch, onMounted, nextTick } from 'vue';
import store from '@/store';
import { config } from '@/config.js';
import { useQueryList } from '@/composables/common/useQueryList';
import { calcularPrecioEntrada } from '@/utils/entradas/precioEntrada';

/**
 * useListadoInvitaciones — listado de pedidos de invitación de una sesión.
 *
 * Traduce el contrato de los endpoints de invitaciones y le suma lo propio del
 * dominio (eventos, selección de pedidos, expansión y generación). La
 * paginación, el debounce, los filtros y la sync con la URL los aporta el
 * genérico `useQueryList`, igual que en `useZonasPegatinas`.
 *
 * Endpoints:
 *  - `back.events.invitaciones`        GET  /eventos/{evento_id}/sesiones/{sesion_id}/invitaciones
 *  - `back.events.invitaciones.eventos` GET /eventos/{evento_id}/sesiones/{sesion_id}/invitaciones/eventos
 *  - `back.events.invitaciones.copiar` POST /eventos/{evento_id}/sesiones/{sesion_id}/invitaciones/copiar
 *
 * El listado sólo filtra en servidor por `search` (concept del primer ticket),
 * `evento_busqueda` y `per_page`; no hay filtros de fecha/estado/modalidad.
 *
 * @param {Object} options
 * @param {number|import('vue').Ref<number>} options.eventoId Id del evento.
 * @param {number|import('vue').Ref<number>} options.sesionId Id de la sesión.
 * @param {Function} [options.fetcher] Inyectable (pedidos). Por defecto despacha el endpoint real.
 * @param {Function} [options.eventosFetcher] Inyectable (eventos). Por defecto despacha el endpoint real.
 * @param {Function} [options.copiarFetcher] Inyectable (generar). Por defecto despacha el endpoint real.
 * @returns {Object} Estado y acciones del listado.
 */
export function useListadoInvitaciones({ eventoId, sesionId, fetcher, eventosFetcher, copiarFetcher } = {}) {
    const error = ref(null);
    const eventos = ref([]);
    // id del pedido abierto; uno por vez.
    const expandedKey = ref(null);
    // mapa id -> true de los pedidos marcados.
    const seleccionados = ref({});
    const loadingGenerar = ref(false);

    const unwrap = value =>
        value && typeof value === 'object' && 'value' in value ? value.value : value;

    const ids = () => ({
        evento_id: unwrap(eventoId),
        sesion_id: unwrap(sesionId),
    });

    /**
     * Petición de pedidos: despacha la acción Vuex del endpoint base.
     * El backend responde `{ data: [...], meta: {...} }`.
     */
    async function dispatchInvitaciones({ filters }) {
        const res = await store.dispatch(config.getVuexAlias('back.events.invitaciones'), {
            filters,
            ...ids(),
        });
        return res.data || { data: [], meta: {} };
    }

    /** Petición de eventos disponibles para el selector. */
    async function dispatchEventos({ filters }) {
        const res = await store.dispatch(config.getVuexAlias('back.events.invitaciones.eventos'), {
            filters,
            ...ids(),
        });
        return (res.data && res.data.data) || [];
    }

    /** Petición de generación: copia las invitaciones de los pedidos marcados. */
    async function dispatchCopiar(payload) {
        const res = await store.dispatch(config.getVuexAlias('back.events.invitaciones.copiar'), payload);
        return (res.data && res.data.data) || [];
    }

    const doFetch = fetcher || dispatchInvitaciones;
    const doFetchEventos = eventosFetcher || dispatchEventos;
    const doCopiar = copiarFetcher || dispatchCopiar;

    /**
     * Fetcher que consume `useQueryList`. Publica el error del endpoint (que
     * `useQueryList` silencia) y degrada a lista vacía para no romper el listado.
     */
    async function pedidosFetcher(params) {
        try {
            const result = await doFetch(params);
            error.value = null;
            return result;
        } catch (e) {
            error.value = e?.response?.data?.message || 'Error al cargar las invitaciones';
            return { data: [], meta: {} };
        }
    }

    // autoFetch: false — primero hay que resolver los eventos y fijar
    // `evento_busqueda`; recién ahí se pide el listado (si no, se pediría todo
    // el teatro sin filtrar y se vería un parpadeo con datos que no son de esta
    // sesión).
    const list = useQueryList({
        fetcher: pedidosFetcher,
        extraFilters: ['evento_busqueda'],
        defaultFilterValues: { evento_busqueda: '' },
        listaIdSource: 'none',
        autoFetch: false,
    });

    const pedidos = computed(() => (list.data.value && list.data.value.data) || []);
    const meta = list.meta;
    const filters = list.filters;
    const loading = list.loading;

    /**
     * Carga los eventos del listado y fija el evento activo. Después dispara el
     * fetch de pedidos de forma inmediata (cancelando el debounce que la propia
     * mutación de `evento_busqueda` programó), para no retrasar la primera carga.
     */
    async function fetchEventos() {
        // `loading` cubre TODO el arranque, no sólo el fetch de pedidos: entre el
        // montaje y la primera respuesta de eventos la tabla tenía `data` vacía y
        // `loading` en false, así que parpadeaba el estado vacío antes del
        // skeleton (vacío → skeleton → vacío). Es un ref compartido con
        // `useQueryList`, que lo vuelve a gestionar cuando dispara `fetch()`.
        loading.value = true;
        error.value = null;
        try {
            const data = await doFetchEventos({ filters: filters.value, ...ids() });
            eventos.value = Array.isArray(data) ? data : [];
        } catch (e) {
            eventos.value = [];
            error.value = e?.response?.data?.message || 'Error al cargar los eventos';
            // Sin eventos no hay nada que filtrar: se corta acá para no pedir el
            // listado sin filtro y dejar el error visible. No hay `fetch()`
            // posterior que apague el loader, así que se apaga a mano.
            loading.value = false;
            return;
        }

        if (!filters.value.evento_busqueda && eventos.value.length > 0) {
            filters.value.evento_busqueda = eventos.value[0].id;

            // El watcher de `useQueryList` corre en el scheduler (nextTick), no
            // en el acto: hay que esperar ese tick para poder cancelar el fetch
            // que agenda. Cancelando antes, el `cancel()` no encuentra nada y el
            // debounce relanza una segunda petición 500ms después, que vuelve a
            // enseñar el skeleton tras el primer resultado.
            await nextTick();
        }

        list.debouncedFetch.cancel();
        await list.fetch();
    }

    const totalSeleccionados = computed(() => Object.keys(seleccionados.value).length);
    const puedeGenerar = computed(() => totalSeleccionados.value > 0);

    /** Marca/desmarca un pedido. */
    function toggleSeleccion(id) {
        const next = { ...seleccionados.value };
        if (next[id]) {
            delete next[id];
        } else {
            next[id] = true;
        }
        seleccionados.value = next;
    }

    /** Abre/cierra el desglose de entradas de un pedido. */
    function toggleExpand(id) {
        expandedKey.value = expandedKey.value === id ? null : id;
    }

    /**
     * Genera las invitaciones de los pedidos marcados. Devuelve el resultado en
     * vez de mostrar modales: la vista decide qué hacer con él (es la vista la
     * que conoce los modales).
     * @returns {Promise<{ok: boolean, orders: Array, errors: Array, message: (string|null)}>}
     */
    async function generarInvitaciones() {
        if (!puedeGenerar.value) {
            return { ok: false, orders: [], errors: [], message: null };
        }

        loadingGenerar.value = true;
        try {
            const orders = await doCopiar({
                filters: {},
                pedidos: { ...seleccionados.value },
                ...ids(),
            });
            seleccionados.value = {};
            return { ok: true, orders: Array.isArray(orders) ? orders : [], errors: [], message: null };
        } catch (e) {
            // El 422 del endpoint trae en `data` los pedidos que no se pudieron
            // generar; cualquier otro fallo (red, 500 sin payload) no trae lista.
            // Se normaliza a array —el modal de errores itera con `v-for`— y se
            // devuelve el `message` para que la vista pueda avisar igual cuando
            // no hay pedidos que listar.
            const detalle = e?.response?.data?.data;

            return {
                ok: false,
                orders: [],
                errors: Array.isArray(detalle) ? detalle : [],
                message: e?.response?.data?.message || null,
            };
        } finally {
            loadingGenerar.value = false;
        }
    }

    /**
     * Precio de una entrada sumando el gasto de gestión, salvo cuando el precio
     * ya los incluye (`price_management_interno`). La regla vive en
     * `utils/entradas/precioEntrada` para no duplicarla con la tabla de entradas.
     */
    const calcularPrecio = calcularPrecioEntrada;

    const columns = [
        { key: 'marcado', label: '', centered: true, class: 'tw:w-16' },
        { key: 'id', label: 'Nª de orden', field: 'id', class: 'tw:w-32' },
        // Reparto en escritorio: `Usuario` lleva ancho fijo porque su contenido
        // (la pastilla de estado + el nº de abonado) es corto y le sobraba hueco;
        // `Taquilla` se queda con TODO el espacio libre, que es donde interesa
        // leer el email, y lo envuelve (`break-all`) para verse completo.
        // En móvil el contenedor es más estrecho que la suma de columnas fijas,
        // así que una `w-auto` quedaría en 0 y las columnas se pisarían: ahí las
        // dos llevan ancho propio (`w-60`) y la tabla scrollea en horizontal.
        { key: 'email_taquillero', label: 'Taquilla', field: 'email_taquillero', class: 'tw:w-60 tw:sm:w-auto tw:sm:break-all' },
        { key: 'usuario', label: 'Usuario', class: 'tw:w-60 tw:sm:w-44', truncate: true },
        { key: 'qr', label: 'Nº', field: 'qr_codes', centered: true, class: 'tw:w-14' },
        { key: 'fecha', label: 'Fecha', field: 'fecha', class: 'tw:w-28' },
        // A `Taquilla` y `Usuario` (que absorben el sobrante) se les deja menos
        // espacio subiendo el ancho fijo de las columnas que iban justas, así el
        // ancho de las dos no se los come todo.
        { key: 'metodo_pago', label: 'Método de pago', field: 'metodo_pago', class: 'tw:w-[124px]' },
        { key: 'modalidad', label: 'Modalidad', field: 'modalidad', class: 'tw:w-24' },
        { key: 'concepto', label: 'Concepto', class: 'tw:w-[108px]', truncate: true },
        { key: 'precio', label: 'Precio + G.G', field: 'precio', class: 'tw:w-20' },
        { key: 'opciones', label: 'Opciones', centered: true, class: 'tw:w-40' },
    ];

    // Cualquier cambio de filtro (evento, búsqueda o página) invalida la
    // selección y el desglose abierto: el pedido marcado puede no estar ya en
    // la página y generar invitaciones de algo que el usuario no ve es peor que
    // obligarlo a re-marcar.
    watch(filters, () => {
        seleccionados.value = {};
        expandedKey.value = null;
    }, { deep: true });

    onMounted(fetchEventos);

    return {
        pedidos,
        loading,
        loadingGenerar,
        error,
        filters,
        meta,
        eventos,
        fetchEventos,
        seleccionados,
        totalSeleccionados,
        puedeGenerar,
        toggleSeleccion,
        expandedKey,
        toggleExpand,
        generarInvitaciones,
        calcularPrecio,
        columns,
    };
}
